import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Thermometer, Droplets, Wind, Gauge, Radio, Database, BellRing, Wrench } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/config/images";

const CAPS = [
  [Gauge, "Differential Pressure Monitoring", "Continuous room and pass-box differential pressure sensing with alarm thresholds."],
  [Wind, "Airflow & Filter Health", "Face velocity trends and filter loading indication on LAF units and booths."],
  [Database, "Data Logging", "Time-stamped parameter history for review, trending and project documentation."],
  [BellRing, "Alarms & Alerts", "Configurable alerts when parameters move outside set limits."],
  [Radio, "Connected Equipment", "Sensor-equipped pass boxes, LAF units and booths with local or remote visibility."],
  [Wrench, "Retrofit Kits", "Monitoring upgrades for existing equipment, subject to site assessment."],
];

export default function SmartSolutions() {
  const series = useMemo(() => {
    let v = 12.4;
    return Array.from({ length: 24 }, (_, h) => {
      v = Math.max(10.8, Math.min(14.2, v + (Math.sin(h * 1.7) * 0.35) + (h % 5 === 0 ? 0.25 : -0.08)));
      return { h: `${String(h).padStart(2, "0")}:00`, pa: Number(v.toFixed(2)) };
    });
  }, []);

  const stats = [
    { icon: Gauge, label: "Differential Pressure", value: "12.4 Pa", ok: true },
    { icon: Thermometer, label: "Temperature", value: "21.8 °C", ok: true },
    { icon: Droplets, label: "Relative Humidity", value: "45 %RH", ok: true },
    { icon: Wind, label: "LAF Face Velocity", value: "0.44 m/s", ok: true },
  ];

  const devices = [
    ["PB-01", "Dynamic Pass Box — Corridor C/D", "Online"],
    ["LF-02", "Vertical LAF — QC Laboratory", "Online"],
    ["DB-01", "Dispensing Booth — Weighing", "Standby"],
  ];

  return (
    <div data-testid="smart-solutions-page">
      <SEO title="Smart & Connected Solutions" path="/smart-solutions" image={IMAGES.iot}
        description="IoT-enabled cleanroom equipment from STJ Innovations: environmental monitoring, data logging, alarms and connected controls for pass boxes, LAF units and booths." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-28">
          <div className="lg:col-span-7">
            <Breadcrumbs dark items={[{ label: "Smart & Connected Solutions" }]} />
            <p className="eyebrow eyebrow-light">Smart / IoT Solutions</p>
            <KineticLines lines={["Smart & Connected", "Solutions"]}
              className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" />
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
              STJ develops IoT-enabled versions of its cleanroom equipment — with environmental monitoring, data logging and connected controls that bring visibility to critical parameters.
            </p>
            <Link to="/request-a-quote?product=IoT-Enabled%20Equipment%20%26%20Monitoring" data-testid="smart-rfq-button"
              className="group mt-8 inline-flex items-center gap-2 bg-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
              Discuss a Connected Requirement <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="spotlight-img overflow-hidden border border-white/10">
                <img src={IMAGES.iot} alt="Sensor and controls integration bench for IoT-enabled equipment" loading="lazy"
                  className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DEMO DASHBOARD */}
      <section className="border-b border-line bg-mist" data-testid="demo-dashboard-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <SectionHead eyebrow="Concept Demonstration" title="Equipment Monitoring Dashboard" />
          <Reveal delay={0.1}>
            <div className="mt-6 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-4 py-2 text-[13px] font-medium text-amber-800" data-testid="demo-data-disclaimer">
              <Activity size={14} /> Demonstration dashboard — sample data only. This does not represent live STJ equipment or a real facility.
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 border border-line bg-ink p-5 sm:p-8" data-testid="demo-dashboard">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-5">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sky-300">STJ Connect — Concept</p>
                  <p className="mt-1 font-display text-lg font-medium text-white">Cleanroom Environment Overview</p>
                </div>
                <span className="border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">
                  Sample Data
                </span>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="border border-white/10 bg-white/[0.04] p-5" data-testid={`demo-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="flex items-center justify-between">
                      <Icon size={17} className="text-sky-400" />
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-label="within range" />
                    </div>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
                    <p className="mt-1.5 font-display text-2xl font-medium text-white">{value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid gap-3 lg:grid-cols-12">
                <div className="border border-white/10 bg-white/[0.04] p-5 lg:col-span-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    Differential Pressure — Last 24 h (Pa)
                  </p>
                  <div className="mt-4 h-56" data-testid="demo-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={series} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis dataKey="h" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} interval={5} />
                        <YAxis domain={[10, 15]} tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{ background: "#0E2438", border: "1px solid rgba(255,255,255,0.12)", fontSize: 12, fontFamily: "JetBrains Mono" }}
                          labelStyle={{ color: "#94A3B8" }} itemStyle={{ color: "#38BDF8" }}
                        />
                        <Area type="monotone" dataKey="pa" stroke="#38BDF8" strokeWidth={1.5} fill="rgba(56,189,248,0.12)" name="Pa" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="border border-white/10 bg-white/[0.04] p-5 lg:col-span-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Connected Units</p>
                  <ul className="mt-4 space-y-3">
                    {devices.map(([id, name, status]) => (
                      <li key={id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-3 last:border-0" data-testid={`demo-device-${id.toLowerCase()}`}>
                        <div>
                          <p className="font-mono text-[11px] text-sky-300">{id}</p>
                          <p className="mt-0.5 text-[13px] text-slate-300">{name}</p>
                        </div>
                        <span className={`shrink-0 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${status === "Online" ? "bg-emerald-400/10 text-emerald-300" : "bg-slate-400/10 text-slate-400"}`}>
                          {status}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-[11px] leading-relaxed text-slate-500">
                    Illustrative interface showing the type of visibility STJ connected equipment is designed to provide.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <SectionHead eyebrow="Capabilities" title="What Connected Equipment Can Do"
          copy="Monitoring and connectivity options are engineered per project — from simple local displays to logged, alarmed and remotely visible installations." />
        <div className="mt-14 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {CAPS.map(([Icon, title, copy], i) => (
            <Reveal key={title} delay={(i % 3) * 0.06} className="bg-white">
              <div className="h-full p-8" data-testid={`smart-cap-${i}`}>
                <Icon size={20} className="text-accent" />
                <h3 className="mt-4 font-display text-lg font-semibold text-ink">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-steel">{copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection title="Planning a Smart or Monitored Installation?"
        copy="Tell us which parameters matter — pressure, airflow, temperature, humidity, particles — and how you want to see them. Our team will propose a monitoring architecture around your equipment and facility." />
    </div>
  );
}
